import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Briefcase, DollarSign, Search, ArrowRight, Star } from 'lucide-react';
import { MatchScoreBadge, ExplanationChips } from '@/components/MatchingUI';

export function RecommendedJobsCard({ jobs = [], navigate, loading = false, title, subtitle, emptyText, emptySubtext, testId = 'recommended-jobs-card' }) {
  const displayJobs = jobs.slice(0, 3);
  const isScored = displayJobs[0]?.final_ranked_score != null;
  const resolvedTitle = title || (isScored ? 'Best Matches' : 'Recommended Jobs');

  if (loading) {
    return (
      <Card className="p-6 border border-slate-200 shadow-sm" data-testid={testId}>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">{resolvedTitle}</h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-slate-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border border-slate-200 shadow-sm rounded-2xl" data-testid={testId}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          {isScored && <Star className="w-5 h-5 text-brand" strokeWidth={1.8} />}
          <h3 className="text-lg font-bold text-slate-900">
            {resolvedTitle}
          </h3>
        </div>
        {displayJobs.length > 0 && (
          <button 
            onClick={() => navigate('/jobs')}
            className="text-xs text-brand hover:text-brand-hover font-semibold flex items-center gap-1"
          >
            View all <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {subtitle && (
        <p className="text-xs text-slate-500 -mt-3 mb-4" data-testid={`${testId}-help`}>
          {subtitle}
        </p>
      )}
      
      {displayJobs.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Search className="w-6 h-6 text-slate-400" />
          </div>
          <p className="text-slate-600 text-sm font-medium mb-1">{emptyText || 'No nearby roles to highlight yet'}</p>
          <p className="text-xs text-slate-400 mb-4">{emptySubtext || 'Browse the full job board to explore more opportunities'}</p>
          <Button
            onClick={() => navigate('/jobs')}
            variant="outline"
            size="sm"
            className="border-brand text-brand hover:bg-brand hover:text-white rounded-lg"
            data-testid={`${testId}-browse-jobs-btn`}
          >
            Browse Job Board
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {displayJobs.map((job) => (
            <button
              key={job.job_id}
              onClick={() => navigate(`/jobs/${job.job_id}`)}
              className="w-full text-left p-3.5 rounded-lg border border-slate-100 hover:border-brand/30 hover:bg-brand-light/30 transition-all group"
              data-testid={`recommended-job-${job.job_id}`}
            >
              <div className="flex items-start gap-3">
                {/* Score */}
                {job.final_ranked_score != null && (
                  <div className="flex flex-col items-center justify-center w-10 h-10 rounded-lg bg-slate-50 group-hover:bg-brand-light flex-shrink-0 transition-colors">
                    <span className={`text-sm font-bold tabular-nums ${
                      job.final_ranked_score >= 70 ? 'text-brand' :
                      job.final_ranked_score >= 40 ? 'text-amber-600' : 'text-slate-400'
                    }`}>{Math.round(job.final_ranked_score)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <h4 className="font-semibold text-sm text-slate-900 truncate group-hover:text-brand transition-colors">{job.title}</h4>
                    {job.opportunity_type === 'skillbridge' && (
                      <span
                        data-testid={`recommended-skillbridge-badge-${job.job_id}`}
                        className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-semibold uppercase tracking-wide"
                      >
                        SkillBridge
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {job.remote ? 'Remote' : job.location}
                    </span>
                    {job.job_type && (
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {job.job_type.replace('_', ' ')}
                      </span>
                    )}
                    {(job.salary_min || job.salary_max) && (
                      <span className="flex items-center gap-1 font-medium text-slate-700">
                        <DollarSign className="w-3 h-3" />
                        {job.salary_min && `$${(job.salary_min / 1000).toFixed(0)}k`}
                        {job.salary_min && job.salary_max && '–'}
                        {job.salary_max && `$${(job.salary_max / 1000).toFixed(0)}k`}
                      </span>
                    )}
                  </div>
                  {job.discovery?.reasons?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`discovery-reasons-${job.job_id}`}>
                      {job.discovery.reasons.map((r) => (
                        <span
                          key={r.key}
                          data-testid={`discovery-reason-${r.key}-${job.job_id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium px-2 py-0.5"
                        >
                          {r.key === 'near_home' && <MapPin className="w-2.5 h-2.5" />}
                          {r.key === 'near_duty' && <MapPin className="w-2.5 h-2.5" />}
                          {r.key === 'regional' && <Star className="w-2.5 h-2.5" />}
                          {r.label}{r.miles != null ? ` · ${r.miles} mi` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {(job.remote || job.explanation?.tags?.length > 0) && (
                    <div className="mt-1.5">
                      <ExplanationChips tags={(() => {
                        // Location is conveyed via discovery.reasons on the Dashboard cards, so
                        // strip legacy location tags (spec: no 'Near home'; no duplicate 'Near Base').
                        const t = (job.explanation?.tags || []).filter(
                          (x) => x !== 'near_home' && x !== 'near_base');
                        if (job.remote && !t.includes('remote_friendly')) return ['remote_friendly', ...t];
                        return t;
                      })()} max={2} />
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isScored && (
        <p className="text-[10px] text-slate-400 mt-3 text-center">Based on your military experience and resume</p>
      )}
    </Card>
  );
}

export default RecommendedJobsCard;
